import { IconComment, IconCornerUpRightLine } from "@synthesis/shared-icons";
import { Button } from "@synthesis/shared-ui";
import { cn } from "@synthesis/shared-utils";
import { observer } from "mobx-react-lite";

import { usePageStore, type ApplicationDetailComment } from "../../application-detail-page.lib";
import { useApplicationComments } from "../../hooks/use-application-comments";
import { DetailTabPlaceholder } from "../detail-tab-placeholder";
import { UserAvatar } from "../user-avatar";
import { UserNameInformation } from "../user-name-information";
import { CommentForm } from "./comment-form";

type CommentsContentProps = {
  className?: string;
};

type CommentCardProps = {
  comment: ApplicationDetailComment;
  onReply?: () => void;
};

const CommentCard = ({ comment, onReply }: CommentCardProps) => (
  <div className="flex items-start gap-3">
    <UserAvatar initials={comment.userInitials} />

    <div className="bg-neutrals-99 flex min-w-0 flex-1 flex-col gap-2 rounded-[14px] p-3">
      <div className="flex min-w-0 items-center gap-1.5">
        <UserNameInformation name={comment.userName} triggerClassName="subtitle-sm text-type-common max-w-[12.75rem] truncate font-medium" />
        <span className="paragraph-sm text-type-light shrink-0">{comment.occurredAt}</span>
      </div>

      <p className="paragraph-sm text-type-minor">{comment.text}</p>

      {comment.canReply && (
        <Button
          type="button"
          leftIcon={<IconCornerUpRightLine className="text-brand-30 size-5" />}
          size="sm"
          theme="text"
          className="w-fit"
          onClick={onReply}
        >
          <span className="text-brand-30">Ответить</span>
        </Button>
      )}
    </div>
  </div>
);

export const CommentsContent = observer(({ className }: CommentsContentProps) => {
  const pageStore = usePageStore();
  const { comments, isLoading, isSubmitting, submitComment } = useApplicationComments();

  if (isLoading) {
    return <p className="paragraph-sm text-type-minor py-4 text-center">Загрузка комментариев...</p>;
  }

  return (
    <div className={cn("flex flex-col gap-2", className)}>
      {comments.length === 0 ? (
        <DetailTabPlaceholder
          title="Комментариев пока нет"
          description="Все комментарии по этой заявке будут отображаться тут"
          icon={<IconComment className="text-neutrals-60 size-12" aria-hidden />}
        />
      ) : (
        comments.map((comment) => {
          const hasReplies = (comment.replies?.length ?? 0) > 0;
          const isReplyOpen = pageStore?.activeReplyId === comment.id;
          const hasIndent = hasReplies || isReplyOpen;

          return (
            <div key={comment.id} className="flex flex-col gap-2">
              <CommentCard comment={comment} onReply={() => pageStore?.toggleActiveReplyId(comment.id)} />

              {hasIndent ? (
                <div className="flex gap-2.5">
                  {hasIndent && <div className="bg-tertiary-80 w-0.5 shrink-0 rounded-sm" aria-hidden />}

                  <div className="flex min-w-0 flex-1 flex-col gap-2">
                    {comment.replies?.map((reply) => (
                      <CommentCard key={reply.id} comment={reply} />
                    ))}

                    {isReplyOpen && (
                      <CommentForm
                        placeholder="Напишите ответ"
                        disabled={isSubmitting}
                        onSubmit={({ comment: replyText }) => submitComment(replyText, comment.id)}
                      />
                    )}
                  </div>
                </div>
              ) : null}
            </div>
          );
        })
      )}

      <CommentForm disabled={isSubmitting} onSubmit={({ comment: commentText }) => submitComment(commentText)} />
    </div>
  );
});
